import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeToRun, getArtifact } from "@/services/multi-agent";
import { sanitizeArtifactLabel } from "@/services/multi-agent/artifactDisplay";
import { invalidateMultiAgentRunsList } from "./useRecentRuns";
import { fetchMultiAgentRunDetail, isMultiAgentAuthError } from "./useRunDetail";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import { extractArtifactCode } from "@/pages/multi-agent/ide/useMultiAgentEditorBridge";
import { useMultiAgentStore, type AgentNodeData, type MultiAgentState, type PlanEdge, type TodoItem } from "@/stores/multiAgentStore";
import {
  useMultiAgentIdeStore,
  type CoveredClass,
  type DependencyItem,
  type DependencySnapshot,
  type ImpactAnalysisEntry,
  type TestCoverageEntry,
} from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

/**
 * Subscribes to a run's SSE stream and maps every event into the store.
 * Handles every event_type from app/stream.py — only ones that affect
 * state. Unknown events are no-ops (forward compatible).
 */
export function useRunStream(runId: string | null) {
  const handleRef = useRef<{ abort: () => void } | null>(null);
  const queryClient = useQueryClient();

  const setStatus = useMultiAgentStore((s) => s.setStatus);
  const setLane = useMultiAgentStore((s) => s.setLane);
  const setBlockedAction = useMultiAgentStore((s) => s.setBlockedAction);
  const setPlan = useMultiAgentStore((s) => s.setPlan);
  const setNodeStatus = useMultiAgentStore((s) => s.setNodeStatus);
  const activateEdge = useMultiAgentStore((s) => s.activateEdge);
  const addPartialFinding = useMultiAgentStore((s) => s.addPartialFinding);
  const setMemoryLoaded = useMultiAgentStore((s) => s.setMemoryLoaded);
  const setSessionContext = useMultiAgentStore((s) => s.setSessionContext);
  const setPromotionTotals = useMultiAgentStore((s) => s.setPromotionTotals);
  const setCost = useMultiAgentStore((s) => s.setCost);
  const setFinalArtifact = useMultiAgentStore((s) => s.setFinalArtifact);
  const setFollowUps = useMultiAgentStore((s) => s.setFollowUps);
  const setProposedFixes = useMultiAgentStore((s) => s.setProposedFixes);
  const appendReasoning = useMultiAgentStore((s) => s.appendReasoning);
  const endReasoning = useMultiAgentStore((s) => s.endReasoning);
  const appendText = useMultiAgentStore((s) => s.appendText);
  const endText = useMultiAgentStore((s) => s.endText);
  const setPlannerFallback = useMultiAgentStore((s) => s.setPlannerFallback);
  const setPlanReused = useMultiAgentStore((s) => s.setPlanReused);
  const appendActivity = useMultiAgentStore((s) => s.appendActivity);
  const setSseStatus = useMultiAgentStore((s) => s.setSseStatus);
  const setSfConnected = useMultiAgentStore((s) => s.setSfConnected);
  const addArtifact = useMultiAgentStore((s) => s.addArtifact);

  // IDE store actions — fed by the MULTI-AGENT run (never dev-agent).
  const addDependency = useMultiAgentIdeStore((s) => s.addDependency);
  const addTestRun = useMultiAgentIdeStore((s) => s.addTestRun);
  const setSourceArtifactId = useMultiAgentIdeStore((s) => s.setSourceArtifactId);
  const addImpactAnalysis = useMultiAgentIdeStore((s) => s.addImpactAnalysis);
  const addTestCoverage = useMultiAgentIdeStore((s) => s.addTestCoverage);
  const setCoverageLines = useMultiAgentIdeStore((s) => s.setCoverageLines);

  useEffect(() => {
    if (!runId) return;
    // Synthetic turns (docs search / MCP pull) reuse the run UI — a non-terminal
    // status drives the shimmer + Narration — but they are NOT streaming backend
    // runs and register no SSE ticket. Don't open a stream or hydrate /detail for
    // them, or it 404s repeatedly on a client-only run id.
    const synthLane = useMultiAgentStore.getState().lane;
    if (synthLane === "docs" || synthLane === "external_record") {
      setSseStatus("idle");
      return;
    }
    // F1b: open the FIRST source class the agent fetches into the editor
    // (dev-agent parity — the referenced file "loads into the chat"). Per-run
    // flag: resets when this effect re-runs for a new runId.
    let openedSourceFile = false;
    // A terminal (past) run is fully hydrated from /detail when you switch to it.
    // Opening a stream to it would reconnect/replay and make the switch look like
    // the previous run is starting again. Only stream runs that are in flight.
    const initialStatus = useMultiAgentStore.getState().status;
    if (initialStatus === "done" || initialStatus === "failed" || initialStatus === "cancelled") {
      handleRef.current?.abort();
      setSseStatus("idle");
      return;
    }
    handleRef.current?.abort();
    const sessionId = useMultiAgentStore.getState().sessionId;

    const hydrateTerminalDetail = async (status: "done" | "failed") => {
      const store = useMultiAgentStore.getState();
      store.finalizeRun(status);
      // The answer text already arrived via the stream; this fetch pulls the
      // full detail (artifacts, fixes). Retry once on a transient failure so a
      // single network blip doesn't leave the run without its artifacts.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const detail = await fetchMultiAgentRunDetail(queryClient, runId);
          if (useMultiAgentStore.getState().runId !== runId) return;
          useMultiAgentStore.getState().hydrateFromDetail(detail, { preserveSession: true });
          await invalidateMultiAgentRunsList(queryClient);
          return;
        } catch (err) {
          if (attempt === 1) {
            console.warn("[multi-agent stream] terminal detail hydrate failed", err);
          } else {
            await new Promise((r) => setTimeout(r, 800));
          }
        }
      }
    };

    // FIX 3 — terminalization safety net. The backend can emit the terminal
    // event (run_completed) yet have it never delivered/handled here (SSE dropped
    // mid-flight, tab throttling, a stray transport error). Without this the UI
    // stays on "running" forever → endless shimmer even though the run finished.
    // Called from onError/onClose when the stream ends while status is still
    // non-terminal: fetch the run's REAL final status ONCE and finalize on it; if
    // that fetch fails but text already arrived, still flip the run terminal so
    // the shimmer clears and the persisted answer shows (never leave it spinning).
    // A ref-guard prevents the two paths (error then close) from double-running.
    let terminalizing = false;
    const terminalizeIfStuck = async (why: string) => {
      if (terminalizing) return;
      const st = useMultiAgentStore.getState();
      if (st.runId !== runId) return;
      if (st.status === "done" || st.status === "failed" || st.status === "cancelled" || st.status === "idle") {
        return; // already terminal — nothing to do
      }
      terminalizing = true;
      try {
        const detail = await fetchMultiAgentRunDetail(queryClient, runId);
        const ds = detail?.run?.status;
        if (useMultiAgentStore.getState().runId !== runId) return;
        if (ds === "done" || ds === "failed" || ds === "cancelled") {
          await hydrateTerminalDetail(ds === "failed" ? "failed" : "done");
          return;
        }
        // Backend still reports non-terminal (genuinely mid-run or unknowable) —
        // if the answer already streamed, treat it as done so the shimmer clears
        // and the answer stays on screen; otherwise leave the 10s poll to retry.
        const cur = useMultiAgentStore.getState();
        if (cur.finalMarkdown.trim() || (cur.textBuffer["synthesizer"] || "").trim()) {
          cur.finalizeRun("done");
        }
      } catch (err) {
        // The status fetch failed (network / auth). Fall back to whatever content
        // arrived: if we have an answer, mark done so the run doesn't hang on the
        // shimmer; if not, don't guess — the 10s poll (or a manual refresh) can
        // still recover it. Auth errors won't recover, so finalize on content.
        console.warn(`[multi-agent stream] terminalize fallback (${why})`, err);
        const cur = useMultiAgentStore.getState();
        if (cur.runId !== runId) return;
        if (cur.finalMarkdown.trim() || (cur.textBuffer["synthesizer"] || "").trim()) {
          cur.finalizeRun("done");
        } else if (isMultiAgentAuthError(err)) {
          // No content AND the run is unreachable (another org / expired session)
          // — it will never recover here, so stop the shimmer by marking failed.
          cur.finalizeRun("failed");
        }
      } finally {
        terminalizing = false;
      }
    };

    // Helper: push an entry to the chronological activity log
    const logActivity = (
      ev: { event_type?: string; event?: string; _event_id?: unknown; emitted_at_ms?: unknown; node_id?: unknown; [k: string]: unknown },
      text: string,
      tone: "info" | "ok" | "warn" | "err" = "info",
      extras?: Partial<{ tool: string; argsSummary: string; durationMs: number; ok: boolean; resultSummary: string }>,
    ) => {
      const id = typeof ev._event_id === "number" ? ev._event_id : 0;
      const ts = typeof ev.emitted_at_ms === "number" ? ev.emitted_at_ms : Date.now();
      const kind = (ev.event_type ?? ev.event ?? "") as string;
      appendActivity({
        id, ts, kind,
        nodeId: typeof ev.node_id === "string" ? ev.node_id : null,
        text, tone,
        ...(extras || {}),
      });
    };

    handleRef.current = subscribeToRun(runId, {
      onEvent: (ev) => {
        const et = (ev.event_type ?? (ev as any).event) as string;
        switch (et) {
          case "subscribed":
          case "heartbeat":
            return;

          case "started":
            setStatus("running");
            logActivity(ev, "Run started", "info");
            return;

          case "lane_picked":
            setLane((ev.lane as string) || "free");
            logActivity(ev, `Lane: ${ev.lane}`, "info");
            return;

          case "blocked":
            // dev-agent parity: org metadata / RAG index not ready. Carries the
            // retrigger action (Start/Retry indexing) or null (in_progress).
            setBlockedAction((ev.action as MultiAgentState["blockedAction"]) ?? null);
            logActivity(ev, `Blocked: ${ev.blocked_by}`, "info");
            return;

          case "auth_required":
          case "sf_token_unauthorized":
            // dev-agent parity: Salesforce token/session failed server-side —
            // flip the pill amber so the user can click "Reconnect SF".
            setSfConnected(false);
            logActivity(ev, "Salesforce session expired — reconnect needed", "warn");
            return;

          case "reasoning_delta":
            appendReasoning(ev.node_id as string, (ev.text as string) || "");
            return;
          case "reasoning_end":
            endReasoning(ev.node_id as string);
            return;

          case "text_delta":
            appendText(ev.node_id as string, (ev.text as string) || "");
            return;
          case "text_message_end":
            endText(ev.node_id as string);
            return;

          case "plan_ready": {
            const plan = ev.plan as { tasks: { id: string; role: string; task: string }[]; edges?: [string, string][] };
            const todo = ev.todo as TodoItem[];
            const nodes: AgentNodeData[] = plan.tasks.map((t) => ({
              id: t.id,
              role: t.role,
              task: t.task,
              status: "planned",
              isSupervisor: t.role === "supervisor",
            }));
            const edges: PlanEdge[] = (plan.edges ?? []).map(([from, to]) => ({ from, to }));
            setPlan(nodes, edges, todo);
            logActivity(ev, `Plan ready (${plan.tasks.length} tasks)`, "info");
            return;
          }
          case "plan_approved":
            useMultiAgentStore.getState().approvePlan();
            logActivity(ev, "Plan approved", "ok");
            return;

          case "node_started":
            setNodeStatus(ev.node_id as string, "running");
            logActivity(ev, `▶ ${ev.node_id} started`, "info");
            return;
          case "node_finished":
            setNodeStatus(ev.node_id as string, (ev.status as "done" | "failed") || "done");
            logActivity(ev, `✓ ${ev.node_id} ${ev.status || "done"}`, ev.status === "failed" ? "err" : "ok");
            return;

          case "tool_call_started":
            setNodeStatus(ev.node_id as string, "tool_calling", (ev.tool as string) || undefined);
            logActivity(
              ev,
              `→ ${ev.tool}(${(ev.args_summary as string) || ""})`,
              "info",
              { tool: ev.tool as string, argsSummary: (ev.args_summary as string) || "" },
            );
            return;
          case "tool_call_completed":
            setNodeStatus(ev.node_id as string, "running", undefined);
            logActivity(
              ev,
              `← ${ev.tool} ${ev.ok ? "ok" : "FAILED"} (${ev.duration_ms}ms)`,
              ev.ok ? "ok" : "err",
              {
                tool: ev.tool as string,
                durationMs: ev.duration_ms as number,
                ok: ev.ok as boolean,
                resultSummary: (ev.error_text as string) || (ev.result_summary as string) || "",
              },
            );
            return;

          case "edge_activated":
            activateEdge(ev.from as string, ev.to as string);
            return;

          case "dispatch_batch":
            // batch of task_ids is starting now
            for (const tid of (ev.task_ids as string[]) || []) {
              setNodeStatus(tid, "running");
            }
            return;

          case "partial_finding":
            addPartialFinding(
              ev.node_id as string,
              (ev.role as string) || "?",
              (ev.summary as string) || "",
            );
            return;

          case "artifact_created": {
            const meta = (ev.metadata as Record<string, unknown>) || {};
            const artifactName = sanitizeArtifactLabel(meta.name, "artifact");
            const artifactId = (ev.artifact_id as string) || "";
            addArtifact({
              id: artifactId,
              type: (meta.type as string) || "other",
              name: artifactName,
              toolName: (meta.tool_name as string) || "",
              byteSize: Number(meta.byte_size || 0),
              createdAt: typeof ev.emitted_at_ms === "number" ? ev.emitted_at_ms as number : Date.now(),
              intake: (meta.intake as Record<string, unknown>) ?? null,
            });
            // Link this artifact to the UUID of the source artifact it was
            // derived from (e.g. a refactor output -> its original source).
            // The base multiAgentStore artifact record has no slot for this,
            // so it lives in the IDE store keyed by artifact id.
            const sourceArtifactId = meta.source_artifact_id;
            if (artifactId && typeof sourceArtifactId === "string" && sourceArtifactId) {
              setSourceArtifactId(artifactId, sourceArtifactId);
            }
            logActivity(ev, `📎 artifact ${artifactName} (${meta.byte_size || 0}B)`, "ok");
            // F1b: when the agent fetches a class's source (get_class_source),
            // open it in the editor so the referenced file "loads into the chat"
            // — ONCE per run, best-effort, content fetched lazily.
            const aType = String(meta.type || "").toLowerCase();
            const aTool = String(meta.tool_name || "").toLowerCase();
            if (
              !openedSourceFile &&
              artifactId &&
              (aType === "apex_class" || aType === "apex_trigger") &&
              (aTool.includes("get_class_source") || aTool.includes("read_source_code"))
            ) {
              openedSourceFile = true;
              void (async () => {
                try {
                  const art = await getArtifact(artifactId);
                  const content = extractArtifactCode(art.content || "");
                  if (content.trim()) {
                    useMultiAgentIdeStore.getState().setWorkspaceCodeRequest({
                      id: artifactId,
                      name: bareClassName(artifactName),
                      content,
                      metadataType: aType === "apex_trigger" ? "ApexTrigger" : "ApexClass",
                      badge: "R",
                    });
                  }
                } catch {
                  /* best-effort: never break the stream on an editor-open */
                }
              })();
            }
            return;
          }

          case "dependencies_ready": {
            const toItems = (raw: unknown): DependencyItem[] =>
              (Array.isArray(raw) ? raw : [])
                .map((r) => {
                  const o = (r ?? {}) as Record<string, unknown>;
                  return {
                    name: String(o.name ?? ""),
                    id: String(o.id ?? ""),
                    metadata_type: String(o.metadata_type ?? "ApexClass"),
                  };
                })
                .filter((it) => it.name);
            const callers = toItems(ev.callers);
            const tests = toItems(ev.tests);
            const snapshot: DependencySnapshot = {
              target: String(ev.target ?? ""),
              target_id: (ev.target_id as string) ?? null,
              callers,
              tests,
              total: callers.length + tests.length,
              status: "awaiting_user_scope",
              captured_at:
                typeof ev.captured_at === "string"
                  ? ev.captured_at
                  : new Date(
                      typeof ev.emitted_at_ms === "number"
                        ? (ev.emitted_at_ms as number)
                        : Date.now(),
                    ).toISOString(),
            };
            if (snapshot.target) {
              addDependency(snapshot);
              logActivity(
                ev,
                `🔗 dependencies for ${snapshot.target} (${callers.length}c · ${tests.length}t)`,
                "ok",
              );
            }
            return;
          }

          case "test_run_completed": {
            const className = String(ev.className ?? "");
            if (!className) return;
            const status = String(ev.status ?? "");
            const success = status === "passed" || status === "Passed";
            const coveredClasses: CoveredClass[] = (
              Array.isArray(ev.coveredClasses) ? ev.coveredClasses : []
            ).map((c) => {
              const o = (c ?? {}) as Record<string, unknown>;
              return {
                name: String(o.name ?? ""),
                coverage_pct:
                  typeof o.coverage_pct === "number" ? (o.coverage_pct as number) : null,
                covered_lines: Array.isArray(o.covered_lines)
                  ? (o.covered_lines as number[])
                  : [],
                uncovered_lines: Array.isArray(o.uncovered_lines)
                  ? (o.uncovered_lines as number[])
                  : [],
                num_covered: Number(o.num_covered ?? 0),
                num_uncovered: Number(o.num_uncovered ?? 0),
              };
            });
            addTestRun({
              testClassName: className,
              className,
              coveredClasses,
              success,
              summary: success ? "Tests passed" : `Tests ${status || "failed"}`,
              ts:
                typeof ev.emitted_at_ms === "number"
                  ? (ev.emitted_at_ms as number)
                  : Date.now(),
            });
            // MA-14: if this agent-driven run covered the file currently open in
            // the editor, light up the Monaco gutter coverage for it.
            const activeEditorClassName =
              useMultiAgentIdeStore.getState().activeEditorClassName;
            const targetBare = bareClassName(activeEditorClassName);
            if (targetBare) {
              const covForEditor = coveredClasses.find(
                (c) => bareClassName(c.name) === targetBare,
              );
              if (covForEditor) {
                setCoverageLines(
                  covForEditor.name,
                  covForEditor.covered_lines || [],
                  covForEditor.uncovered_lines || [],
                  covForEditor.coverage_pct,
                );
              }
            }
            logActivity(
              ev,
              `🧪 ${className} ${status || "done"} (${ev.coveragePct ?? 0}%)`,
              success ? "ok" : "warn",
            );
            return;
          }

          case "impact_analysis": {
            // Post-save impact analysis for a saved class. Mirrors the
            // dev-agent `runPostSaveAnalysis` impact block; populates the IDE
            // store so the copied ImpactPanel renders it (MA-9).
            const className = String(ev.class_name ?? ev.className ?? "");
            if (!className) return;
            const toCallers = (raw: unknown) =>
              (Array.isArray(raw) ? raw : []).map((r) => {
                const o = (r ?? {}) as Record<string, unknown>;
                const risk = String(o.risk ?? "low");
                return {
                  name: String(o.name ?? ""),
                  type: String(o.type ?? "ApexClass"),
                  risk: (risk === "high" || risk === "medium" ? risk : "low") as
                    | "high"
                    | "medium"
                    | "low",
                };
              });
            // The backend emits a NESTED payload: {class_name, metadata_type,
            // impact:{...}}. Read the real fields from ev.impact; fall back to ev
            // for forward-compat if the backend is ever changed to flatten.
            const impact = (ev.impact && typeof ev.impact === "object"
              ? ev.impact
              : ev) as Record<string, unknown>;
            const entry: ImpactAnalysisEntry = {
              class_name: className,
              blast_radius:
                typeof impact.blast_radius === "number"
                  ? (impact.blast_radius as number)
                  : undefined,
              callers: toCallers(impact.callers),
              callees: (Array.isArray(impact.callees) ? impact.callees : []).map((c) => {
                const o = (c ?? {}) as Record<string, unknown>;
                return { name: String(o.name ?? ""), type: String(o.type ?? "") };
              }),
              data_access: (Array.isArray(impact.data_access) ? impact.data_access : []).map((d) => {
                const o = (d ?? {}) as Record<string, unknown>;
                return {
                  object: String(o.object ?? ""),
                  operations: Array.isArray(o.operations)
                    ? (o.operations as string[])
                    : [],
                };
              }),
              triggers_at_risk: (Array.isArray(impact.triggers_at_risk) ? impact.triggers_at_risk : []).map((t) => {
                const o = (t ?? {}) as Record<string, unknown>;
                return {
                  trigger: String(o.trigger ?? ""),
                  object: String(o.object ?? ""),
                };
              }),
              references: (Array.isArray(impact.references) ? impact.references : []).map((r) => {
                const o = (r ?? {}) as Record<string, unknown>;
                return {
                  file: String(o.file ?? ""),
                  line: Number(o.line ?? 0),
                  kind: String(o.kind ?? ""),
                };
              }),
              summary: typeof impact.summary === "string" ? (impact.summary as string) : undefined,
            };
            addImpactAnalysis(entry);
            logActivity(
              ev,
              `⚡ impact for ${className} (blast ${entry.blast_radius ?? 0})`,
              "ok",
            );
            return;
          }

          case "test_coverage": {
            // Post-save aggregate coverage for a saved class. Populates the IDE
            // store (surfaced in TestsPanel). If line-level coverage is carried,
            // also drive the Monaco gutter decorations via setCoverageLines (MA-7).
            const className = String(ev.class_name ?? ev.className ?? "");
            if (!className) return;
            // The backend emits a NESTED payload: {class_name, metadata_type,
            // coverage:{...}}. Read the real fields from ev.coverage; fall back
            // to ev for forward-compat if the backend is ever flattened.
            const coverage = (ev.coverage && typeof ev.coverage === "object"
              ? ev.coverage
              : ev) as Record<string, unknown>;
            const entry: TestCoverageEntry = {
              class_name: className,
              test_classes: (Array.isArray(coverage.test_classes) ? coverage.test_classes : []).map((t) => {
                const o = (t ?? {}) as Record<string, unknown>;
                return {
                  name: String(o.name ?? ""),
                  coverage_pct:
                    typeof o.coverage_pct === "number"
                      ? (o.coverage_pct as number)
                      : null,
                  lines_covered:
                    typeof o.lines_covered === "number"
                      ? (o.lines_covered as number)
                      : null,
                  lines_uncovered:
                    typeof o.lines_uncovered === "number"
                      ? (o.lines_uncovered as number)
                      : null,
                };
              }),
              aggregate_coverage_pct:
                typeof coverage.aggregate_coverage_pct === "number"
                  ? (coverage.aggregate_coverage_pct as number)
                  : null,
              summary: typeof coverage.summary === "string" ? (coverage.summary as string) : undefined,
            };
            addTestCoverage(entry);
            // If the event carries line-level coverage for the saved class,
            // light up the Monaco gutter (MA-14 path also lands here).
            const covered = Array.isArray(coverage.covered_lines)
              ? (coverage.covered_lines as number[])
              : null;
            const uncovered = Array.isArray(coverage.uncovered_lines)
              ? (coverage.uncovered_lines as number[])
              : null;
            if (covered || uncovered) {
              setCoverageLines(
                className,
                covered ?? [],
                uncovered ?? [],
                entry.aggregate_coverage_pct ?? null,
              );
            }
            logActivity(
              ev,
              `📊 coverage for ${className} (${entry.aggregate_coverage_pct ?? 0}%)`,
              "ok",
            );
            return;
          }

          case "validation_result": {
            const kind = (ev.kind as string) || "validation";
            const summary = (ev.summary as string) || "";
            const finding_count = (ev.finding_count as number) || 0;
            const ok = (ev.ok as boolean) ?? true;
            const tag = ok ? "validation" : "validation_failed";
            const text = finding_count
              ? `${kind} (${finding_count}): ${summary}`
              : `${kind}: ${summary}`;
            addPartialFinding((ev.node_id as string) || "?", tag, text);
            logActivity(ev, `⚠ ${text}`, ok ? "warn" : "err");
            return;
          }

          case "supervisor_spawning": {
            const parent = ev.parent_id as string;
            const children = (ev.children as Array<{ id: string; role: string; task: string }>) || [];
            // Append the spawned children to the graph store so they appear
            // as planned nodes connected to the parent.
            const store = useMultiAgentStore.getState();
            const existingNodes = store.nodes;
            const newNodes = { ...existingNodes };
            const newEdges = [...store.edges];
            for (const c of children) {
              if (!newNodes[c.id]) {
                newNodes[c.id] = {
                  id: c.id,
                  role: c.role,
                  task: c.task,
                  status: "planned",
                  isSupervisor: c.role === "supervisor",
                };
                newEdges.push({ from: parent, to: c.id });
              }
            }
            useMultiAgentStore.setState({
              nodes: newNodes,
              edges: newEdges,
              todo: [
                ...store.todo,
                ...children
                  .filter((c) => !store.todo.some((t) => t.id === c.id))
                  .map((c) => ({ id: c.id, label: c.task, status: "queued" as const })),
              ],
            });
            return;
          }

          case "memory_loaded":
            setMemoryLoaded({
              similarRuns: (ev.similar_runs as number) || 0,
              knownFacts: (ev.known_facts as number) || 0,
              recentGaps: (ev.recent_gaps as number) || 0,
            });
            return;

          case "session_loaded": {
            const turnCount = Number(ev.turn_count || 0);
            const artifactCount = Number(ev.artifact_count || 0);
            const badToolCount = Number(ev.bad_tool_count || 0);
            const compressed = Boolean(ev.compressed);
            setSessionContext({
              turnCount,
              artifactCount,
              badToolCount,
              compressed,
            });
            logActivity(
              ev,
              `Loaded ${turnCount} prior turn${turnCount === 1 ? "" : "s"} into context${compressed ? " with compression" : ""}`,
              "ok",
            );
            return;
          }

          case "session_compressed": {
            const keptTurns = Number(ev.kept || 0);
            const compressedChars = Number(ev.compressed_chars || 0);
            const prev = useMultiAgentStore.getState().sessionContext;
            setSessionContext({
              turnCount: prev?.turnCount || keptTurns,
              artifactCount: prev?.artifactCount || 0,
              badToolCount: prev?.badToolCount || 0,
              compressed: true,
              compressedChars,
              keptTurns,
            });
            logActivity(
              ev,
              `Compressed prior conversation (${keptTurns} turn${keptTurns === 1 ? "" : "s"} kept)`,
              "ok",
            );
            return;
          }

          case "cost_update":
            setCost({
              usedUsd: (ev.used_usd as number) || 0,
              maxUsd: (ev.max_usd as number) || 5,
              tokensIn: (ev.tokens_in as number) || 0,
              tokensOut: (ev.tokens_out as number) || 0,
              toolCalls: (ev.tool_calls as number) || 0,
            });
            return;
          case "cost_exceeded":
            // surfaced as a banner — graph already updated cost
            return;
          case "synthesis_done":
            if (ev.result_artifact_id) {
              setFinalArtifact(ev.result_artifact_id as string);
            }
            return;

          case "followup_suggestions":
            // Conversation-grounded chips from the backend — replace the static
            // templates. Shape: { suggestions: [{label, hint, question}] }.
            setFollowUps(
              (ev.suggestions as Array<{ label: string; hint: string; question: string }>) || [],
            );
            return;

          case "verifier_done":
            useMultiAgentStore.getState().setVerifier({
              ok: Boolean(ev.ok),
              score: Number(ev.score ?? 0),
              gaps: ((ev.gaps as string[]) || []).slice(0, 10),
              noEvidence: Boolean(ev.no_evidence),
              replanCount: Number((ev.replan_count as number) ?? 0),
            });
            return;

          case "synth_contract_violation":
            useMultiAgentStore.getState().setContractViolation({
              lane: (ev.lane as string) || "?",
              violations: ((ev.violations as string[]) || []).slice(0, 6),
            });
            return;

          case "auto_fix_failed":
            useMultiAgentStore.getState().setAutoFixFailed({
              reason: (ev.reason as string) || "unknown",
            });
            return;

          case "fixes_proposed":
            setProposedFixes(
              ((ev.summaries as Array<{
                kind: string; target: string; risk: string; summary: string;
              }>) || []),
            );
            return;

          case "promoted":
            setPromotionTotals({
              factCount: (ev.fact_count as number) || 0,
              artifactCount: (ev.artifact_count as number) || 0,
            });
            return;

          case "planner_fallback":
            setPlannerFallback({
              reason: (ev.reason as string) || "planner produced no usable plan",
              rawPreview: (ev.raw_preview as string) || "",
            });
            return;

          case "plan_reused":
            setPlanReused({
              fromRunId: (ev.from_run_id as string) || "",
              similarity: Number(ev.similarity ?? 0),
              lane: (ev.lane as string) || "",
            });
            return;

          case "plan_rejected":
            // soft visual — actual abort comes via run_cancelled below
            return;

          case "run_cancelled":
            // Drive through the optimistic path so the cancel feels the
            // same whether the user clicked Stop or the cancel arrived
            // from another tab / api caller.
            useMultiAgentStore.getState().markCancelled();
            logActivity(ev, "Run cancelled", "warn");
            return;

          case "done":
          case "run_completed":
            void hydrateTerminalDetail("done");
            return;
          case "run_failed":
          case "budget_exceeded":
          // completed_with_no_evidence is its own terminal kind on the
          // backend (iter 7+) — DB status is `failed` and the UI shows
          // the no-evidence banner. Missing this case left the store in
          // "running" until onError tripped, then mis-rendered as Failed.
          case "completed_with_no_evidence":
            void hydrateTerminalDetail("failed");
            return;

          default:
            return;
        }
      },
      onError: (err) => {
        console.warn("[multi-agent stream] error", err);
        // Don't eager-flip to "failed" on a stream/transport error — the run may
        // still be alive server-side (the SSE loop already retried, including a
        // bearer-auth fallback, before calling onError). Mark the stream
        // disconnected, then IMMEDIATELY reconcile the REAL status from /detail
        // (FIX 3) instead of waiting up to 10s for the poll — the terminal event
        // may simply have been dropped, and a stuck shimmer is the symptom.
        setSseStatus("disconnected");
        void terminalizeIfStuck("stream error");
      },
      onClose: () => {
        handleRef.current = null;
        // Stream closes naturally on every terminal event. Don't show that
        // as "disconnected" — by the time onClose fires, the store's status
        // is already done/failed/cancelled and the pill should hide.
        const st = useMultiAgentStore.getState().status;
        if (st === "done" || st === "failed" || st === "cancelled") {
          setSseStatus("idle");
        } else {
          // Stream closed while the run is still non-terminal — the terminal
          // event wasn't delivered/handled. Reconcile once and terminalize so
          // the shimmer clears (FIX 3), rather than sitting on "running".
          setSseStatus("disconnected");
          void terminalizeIfStuck("stream close");
        }
      },
      onStatus: (s) => setSseStatus(s),
    }, undefined, sessionId);

    // Safety-net poll: on long runs the SSE can drop BEFORE the terminal event,
    // leaving the UI stuck on "running" until a manual refresh. Every 10s, if the
    // run is still non-terminal AND the SSE is NOT actively connected, ask the
    // backend and finalize if it actually completed. Gating on sseStatus avoids
    // ~one redundant /detail fetch per 10s for the whole life of a healthy run —
    // when the stream is connected it will deliver the terminal event itself.
    const pollTimer = setInterval(() => {
      const st = useMultiAgentStore.getState();
      if (st.runId !== runId) return;
      if (st.status === "done" || st.status === "failed" || st.status === "cancelled" || st.status === "idle") return;
      // SSE is healthy — let the stream finalize; skip the network probe.
      if (st.sseStatus === "connected" || st.sseStatus === "connecting") return;
      void (async () => {
        try {
          const detail = await fetchMultiAgentRunDetail(queryClient, runId);
          const ds = detail?.run?.status;
          if (
            (ds === "done" || ds === "failed" || ds === "cancelled") &&
            useMultiAgentStore.getState().runId === runId
          ) {
            await hydrateTerminalDetail(ds === "failed" ? "failed" : "done");
          }
        } catch (err) {
          // Auth errors won't recover (run belongs to another org / session
          // expired) — stop polling so we don't spam 401/403s in the console.
          // Other (transient) errors: let the next tick retry.
          if (isMultiAgentAuthError(err)) clearInterval(pollTimer);
        }
      })();
    }, 10000);

    return () => {
      clearInterval(pollTimer);
      handleRef.current?.abort();
      handleRef.current = null;
    };
  }, [queryClient, runId]);
}
